import i18n from '@/plugins/i18n';
import type { AdmissionPasswordStatus } from './admission-map-types';

export type AutocompleteOption<T = unknown> = {
  label: string;
  value: T;
  [key: string]: any;
};

const translate = (key: string) => String(i18n.t(key));

export const getBooleanOptions = (): AutocompleteOption<boolean>[] => [
  { label: translate('admissionMap.boolean.yes'), value: true },
  { label: translate('admissionMap.boolean.no'), value: false },
];

export const formatOptionLabel = (
  value: unknown,
  options: AutocompleteOption<string>[],
): string => {
  if (value === null || value === undefined || value === '') return '';
  const str = String(value);
  const match = options.find((o) => o.value === str);
  return match?.label ?? str;
};

const isHighCostNoOption = (option: AutocompleteOption<string>): boolean => {
  const normalize = (raw: unknown) => String(raw ?? '')
    .trim()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();

  const label = normalize(option.label);
  const value = normalize(option.value);

  return label === 'nao' || value === 'nao';
};

export const sortHighCostTypeOptions = (
  options: AutocompleteOption<string>[],
): AutocompleteOption<string>[] => {
  const noOption = options.find(isHighCostNoOption);
  if (!noOption) return options;

  return [...options.filter((option) => option !== noOption), noOption];
};

const historyRawIsEmpty = (raw: unknown): boolean => raw === null || raw === undefined || raw === '';

export const formatHighCostHistoryValue = (
  raw: unknown,
  options: AutocompleteOption<string>[],
): string => {
  const label = formatOptionLabel(raw, options);
  if (label !== String(raw)) return label;
  if (raw === true || String(raw).toLowerCase() === 'true') {
    return translate('admissionMap.boolean.yes');
  }
  if (raw === false || String(raw).toLowerCase() === 'false') {
    return translate('admissionMap.boolean.no');
  }
  if (historyRawIsEmpty(raw)) return '';
  return String(raw);
};

export const formatUrgencyTypeHistoryValue = (
  raw: unknown,
  options: AutocompleteOption<string>[],
): string => {
  if (historyRawIsEmpty(raw)) return '';
  return formatOptionLabel(raw, options);
};

export const getPasswordStatusOptions = (): AutocompleteOption<AdmissionPasswordStatus>[] => [
  { label: translate('admissionMap.passwordStatuses.authorized'), value: 'AUTORIZADO' },
  { label: translate('admissionMap.passwordStatuses.released'), value: 'LIBERADA' },
  { label: translate('admissionMap.passwordStatuses.denied'), value: 'NEGADO' },
  { label: translate('admissionMap.passwordStatuses.denied'), value: 'NEGADA' },
  { label: translate('admissionMap.passwordStatuses.pending'), value: 'PENDENTE' },
  { label: translate('admissionMap.passwordStatuses.canceled'), value: 'CANCELADO' },
  { label: translate('admissionMap.passwordStatuses.canceled'), value: 'CANCELADA' },
  { label: translate('admissionMap.passwordStatuses.analysis'), value: 'EM ANÁLISE' },
];

export const formatPasswordStatus = (status?: AdmissionPasswordStatus | null): string => {
  if (!status) return '-';
  const match = getPasswordStatusOptions().find((option) => option.value === status);
  return match ? match.label : status;
};

export const formatBooleanValue = (value?: boolean | null): string => {
  if (value === null || value === undefined) return '';
  return value
    ? translate('admissionMap.boolean.yes')
    : translate('admissionMap.boolean.no');
};
